import { getActiveDeliveryWindowsIdsForOrderBuyer, getAllDeliveryWindows, getOrderDeliveryWindowsIds } from '@app/ducks';
import { getDeliveryWindowsByIds, getIsExpired } from '@app/logic/deliveryWindows';
import { getUnitsCountInDelwin } from '@app/logic/Orders';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Checkout, Order } from '@typings';
import React from 'react';
import { useSelector } from 'react-redux';

import { CheckoutShippingPreferencesByOrder } from './CheckoutShippingPreferencesByOrder';
import { CheckoutShippingPreferencesCombined } from './CheckoutShippingPreferencesCombined';
import { CheckoutShippingPreferencesSkeleton } from './CheckoutShippingPreferencesSkeleton';

interface Props {
  checkoutFields: Checkout.Requirements;
  orderDetails: Order.Single;
  isLoading: boolean;
}

export const CheckoutShippingPreferences = ({ checkoutFields, orderDetails, isLoading }: Props) => {
  const { order, splitOrders } = orderDetails;

  const allDeliveryWindows = useSelector(getAllDeliveryWindows);
  const orderDeliveryWindowsIds = useSelector(getOrderDeliveryWindowsIds);
  const activeDeliveryWindowsIds = useSelector(getActiveDeliveryWindowsIdsForOrderBuyer);

  const hasAnyPreferredShippingDate =
    isDefined(checkoutFields.preferredShippingDate) || isDefined(checkoutFields.preferredShippingDateBySplitOrder);
  const hasAnyCancelShippingDate = isDefined(checkoutFields.cancelDate) || isDefined(checkoutFields.cancelDateBySplitOrder);
  const hasAnyDate = hasAnyPreferredShippingDate || hasAnyCancelShippingDate;
  const hasOrderShippingMethods = isDefined(order.shippingMethods) && !isEmpty(order.shippingMethods);
  const hasAnyShippingMethods =
    hasOrderShippingMethods || (isDefined(splitOrders?.[0]?.shippingMethods) && !isEmpty(splitOrders[0].shippingMethods));

  const getDeliveryWindowsWithProducts = React.useCallback(
    (deliveryWindowIds: string[]) => {
      const deliveryWindows = getDeliveryWindowsByIds(allDeliveryWindows, deliveryWindowIds);

      return deliveryWindows.filter(deliveryWindow => {
        const hasUnits = getUnitsCountInDelwin(deliveryWindow.deliveryWindow, orderDetails.order.products) > 0;
        const isExpired = getIsExpired(deliveryWindow, activeDeliveryWindowsIds);

        return hasUnits && !isExpired;
      });
    },
    [activeDeliveryWindowsIds, allDeliveryWindows, orderDetails.order.products],
  );

  if (!hasAnyDate && !hasAnyShippingMethods) {
    return null;
  }

  if (isLoading) {
    return <CheckoutShippingPreferencesSkeleton />;
  }

  const getSplitOrderShippingMethods = (splitOrder: Order.Open, index: number) => {
    const isFirstOrder = index === 0;

    // If the main order has shipping methods it means that shipping cost is applied only on the first order
    if (hasOrderShippingMethods && isFirstOrder) {
      return order.shippingMethods ?? [];
    }

    return splitOrder.shippingMethods ?? [];
  };

  const getSplitOrderSelectedShippingMethods = (splitOrder: Order.Open, index: number) => {
    const isFirstOrder = index === 0;

    // If the main order has shipping methods it means that shipping cost is applied only on the first order
    if (hasOrderShippingMethods && isFirstOrder) {
      return order.selectedShippingMethods ?? [];
    }

    return splitOrder.selectedShippingMethods ?? [];
  };

  return isDefined(splitOrders) && !isEmpty(splitOrders) ?
      splitOrders.map((splitOrder, index) => (
        <CheckoutShippingPreferencesByOrder
          orderIndex={splitOrders.length > 1 ? index + 1 : undefined}
          orderId={order.order}
          splitId={splitOrder.order}
          checkoutFields={checkoutFields}
          deliveryWindows={getDeliveryWindowsWithProducts(splitOrder.deliveryWindows)}
          shippingMethods={getSplitOrderShippingMethods(splitOrder, index)}
          selectedShippingMethods={getSplitOrderSelectedShippingMethods(splitOrder, index)}
          key={`shippingPreferences-${splitOrder.order}`}
        />
      ))
    : <CheckoutShippingPreferencesCombined
        checkoutFields={checkoutFields}
        orderId={order.order}
        deliveryWindows={getDeliveryWindowsWithProducts(orderDeliveryWindowsIds)}
        shippingMethods={order.shippingMethods ?? []}
        selectedShippingMethods={order.selectedShippingMethods ?? []}
    />;
};
